import { HttpInterceptorFn } from "@angular/common/http";
import { PLATFORM_ID, inject } from "@angular/core";
import { isPlatformServer } from "@angular/common";
import { REQUEST } from "ngx-cookie-service-ssr";
import { apiBaseUrl } from "src/app/core/helpers/api-base-url";

/**
 * During SSR, HttpClient calls to our own backend are fresh server-to-server
 * requests that carry NONE of the browser's cookies. Under the gated cutover,
 * the load balancer routes by the `jovis_route` cookie — so without it the SSR
 * data fetch falls through to the default (frozen clone) backend and the page
 * renders stale data, even though the browser's own (cookie-bearing) calls hit
 * the new backend.
 *
 * This interceptor runs ONLY on the server and ONLY for our API host
 * (`apiBaseUrl()`, the same base the calls are built from), forwarding the
 * inbound request's Cookie header so the SSR fetch is routed to the same
 * instance that served the page. It is a no-op on the client (the browser
 * attaches cookies itself) and harmless post-cutover (no `jovis_route` cookie →
 * normal routing). Scoped to the API base so cookies are never forwarded to
 * third-party hosts (WP translations, ip-country, FoxyCart, etc.).
 *
 * The match has to use `apiBaseUrl()`, not `environment.apiUrl`: with
 * `SSR_API_URL` set the server-side calls carry the loopback base, and matching
 * the public URL would silently stop forwarding. Loopback also reaches this
 * box's own backend directly, which is what the cookie was steering the request
 * to in the first place.
 */
export const ssrApiCookieForwardInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isPlatformServer(inject(PLATFORM_ID))) return next(req);
  if (!req.url.startsWith(apiBaseUrl())) return next(req);

  const request = inject(REQUEST, { optional: true }) as
    | { headers?: { cookie?: string } }
    | null;
  const cookie = request?.headers?.cookie;
  if (!cookie) return next(req);

  return next(req.clone({ setHeaders: { Cookie: cookie } }));
};
